export const VALIDATION = {
  required: 'required',
  requiredGroup: 'requiredGroup',
  requiredContact: 'requiredContact',
  numberOfDigits: 'numberOfDigits',
  numberOfAlphaNumeric: 'numberOfAlphaNumeric',
  email: 'email',
  phoneNumber: 'phoneNumber',
  string: 'string',
  number: 'number',
  selectWithChildren: 'selectWithChildren',
  maxCharacters: 'maxCharacters',
  array: 'array',
  requiredValidation: 'requiredValidation',
  requiredCOA: 'requiredCOA',
  cardEmail: 'cardEmail'
};

/**
 * @param {string} value - Value to test validation
 * @param {string} name - Value to test validation
 * @param {boolean} required - Boolean that specified that the value needs to exist
 * @param {{ type: string, param: any}[]} validations - Array of objects with type of validation and param if any
 * @param {function} t
 * @param {string[]} errors - Errors array to store error message if it doesn't pass validation
 */
export function validate(value, validations, required, t, store, contactCardName, errors = []) {
  // If requried is present, add it to the validation array
  if (required) {
    validations = [{ type: VALIDATION.required }, ...validations];
  }

  // If there is no validations then return empty string (no error)
  if (validations.length === 0) {
    return '';
  }

  // Loop through validations and add errors to the array
  for (let validation of validations) {
    switch (validation.type) {
      case VALIDATION.required:
        validateRequired(value, t, errors);
        break;
      case VALIDATION.requiredGroup:
        validateRequiredGroup(value, t, errors);
        break;
      case VALIDATION.numberOfDigits:
        validateNumberOfDigits(value, validation.param, t, errors);
        break;
      case VALIDATION.numberOfAlphaNumeric:
        validateNumberOfAlphaNumeric(value, validation.param, t, errors);
        break;
      case VALIDATION.maxCharacters:
        validateCharacterCount(value, validation.param, t, errors);
        break;
      case VALIDATION.email:
        validateEmail(value, t, errors);
        break;
      case VALIDATION.cardEmail:
        validateCardEmail(value, t, errors);
        break;
      case VALIDATION.phoneNumber:
        validatePhoneNumber(value, t, errors);
        break;
      case VALIDATION.string:
        validateString(value, t, errors);
        break;
      case VALIDATION.number:
        validateNumber(value, t, errors);
        break;
      case VALIDATION.array:
        errors = validateArrayValues(value, validation.param, t, errors);
        break;
      case VALIDATION.requiredValidation:
        validateTest(value, t, errors);
        break;
      case VALIDATION.requiredCOA:
        validateContactCards(t, errors, store, contactCardName);
        break;
      case VALIDATION.requiredPackingList:
        validateContactCards(t, errors, store, contactCardName);
        break;
      case VALIDATION.requiredOrderConfirmation:
        validateContactCards(t, errors, store, contactCardName);
        break;
      default:
        break;
    }
  }
  // Return the first error (prioritized) or empty string (no error)
  return errors[0] ? errors[0] : '';
}

/**
 * @param {string} value - Value to test validation
 * @param {function} t - translation
 * @param {string[]} errors - Errors array to store error message if it doesn't pass validation
 */
export function validateRequired(value, t, errors) {
  const isArrayValue = Array.isArray(value);
  const validCondition = isArrayValue ? value.length === 0 : !value;
  if (validCondition) {
    errors.push(t('errors.required'));
  }
}

/**
 * @param {string} value - Value to test validation
 * @param {function} t - translation
 * @param {string[]} errors - Errors array to store error message if it doesn't pass validation
 */
export function validateRequiredGroup(value, t, errors) {
  if (
    value &&
    !Object.values(value).some(item => {
      if (Array.isArray(item)) {
        return item.length;
      }
      return item;
    })
  ) {
    errors.push(t('errors.requiredGroup'));
  }
}

/**
 * @param {string} value - Value to test validation
 * @param {function} t - translation
 * @param {string[]} errors - Errors array to store error message if it doesn't pass validation
 */
export function validateTest(value, t, errors) {
  const hasEmail = Array.isArray(value?.email) ? value.email.length > 0 : Boolean(value?.email);
  const hasFax = Boolean(value?.fax);
  const hasMailToBillTo = Boolean(value?.mailToBillTo);

  if (!hasEmail && !hasFax && !hasMailToBillTo) {
    errors.push(t('errors.requiredGroup'));
  }
}

export function validateContactCards(t, errors, store, contactCardName) {
  const contactCardStore = store?.customer?.shipTo?.[0]?.[contactCardName];
  if (contactCardStore) {
    const { email, fax, method } = contactCardStore;
    // Check if email is empty and fax is empty
    method === 'New Contact' && email?.length === 0 && fax?.length === 0 && errors.push(t('errors.requiredContact'));
  }
}

/**
 * @param {string} value - Value to test validation
 * @param {number} numberOfDigits - Number of digits required to pass validation, single value or array
 * @param {function} t - translation
 * @param {string[]} errors - Errors array to store error message if it doesn't pass validation
 */
export function validateNumberOfDigits(value, numberOfDigits, t, errors) {
  if (!/^[0-9]{1,255}$/i.test(value) && value) {
    errors.push(t('errors.numbersOnly'));
  }
  // Wraps value into string in order to split and check number of digits
  const count = `${value}`.split('').length;

  if (![numberOfDigits].flat().includes(count) && value) {
    // Format error message with ' or ' between numberOfDigits items if an array was passed instead of a single value
    const n = `${numberOfDigits}`.replace(',', ' or ');
    errors.push(t('errors.length', { n: n }));
  }
}

/**
 * @param {string} value - Value to test validation
 * @param {number} numberOfCharacters - Number of characters required to pass validation, single value or array
 * @param {function} t - translation
 * @param {string[]} errors - Errors array to store error message if it doesn't pass validation
 */
export function validateNumberOfAlphaNumeric(value, numberOfCharacters, t, errors) {
  if (!/^[0-9a-zA-Z]{1,255}$/i.test(value) && value) {
    errors.push(t('errors.alphaNumericOnly'));
  }

  const count = value && value.toString().length;
  if (numberOfCharacters && ![numberOfCharacters].flat().includes(count) && value) {
    const min = numberOfCharacters[0];
    const max = numberOfCharacters[numberOfCharacters.length - 1];
    const minMax = [min, max];
    // Format error message with ' or ' between numberOfDigits items if an array was passed instead of a single value
    const n = `${minMax}`.replace(',', ' to ');
    errors.push(t('errors.minMax', { n: n }));
  }
}

/**
 * @param {string} value - Value needed to test phone number validation
 * @param {function} t - translation
 * @param {string[]} errors - An array of errors to render the error message if it does not pass validation
 */
export function validatePhoneNumber(value, t, errors) {
  if (!/^\d{3}-\d{3}-\d{4}$/i.test(value) && value) {
    errors.push(t('errors.phoneNumber'));
  }
}

/**
 * @param {string} value - Value needed to test string validation
 * @param {function} t - translation
 * @param {string[]} errors - An array of errors to render the error message if it does not pass validation
 */
export function validateString(value, t, errors) {
  if (!/^[a-zA-Z0-9._]{1,255}$/i.test(value) && value) {
    errors.push(t('errors.charactersNumbersOnly'));
  }
}

/**
 * @param {string} value - Value needed to test number validation
 * @param {function} t - translation
 * @param {string[]} errors - An array of errors to render the error message if it does not pass validation
 */
export function validateNumber(value, t, errors) {
  if (!/^[0-9]{1,255}$/i.test(value) && value) {
    errors.push(t('errors.numbersOnly'));
  }
}

/**
 * @param {string} value - Value needed to test email validation
 * @param {function} t - translation
 * @param {string[]} errors - An array of errors to render the error message if it does not pass validation
 */
export function validateEmail(value, t, errors) {
  if (
    !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,3}))$/i.test(
      value
    ) &&
    value
  ) {
    errors.push(t('errors.email'));
  }
}
/**
 * will only required for contact cards as there email can have characters between 2 to 10 after "."
 * @param {string} value - Value needed to test email validation
 * @param {function} t - translation
 * @param {string[]} errors - An array of errors to render the error message if it does not pass validation
 */
export function validateCardEmail(value, t, errors) {
  if (
    !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,10}))$/i.test(
      value
    ) &&
    value
  ) {
    errors.push(t('errors.email'));
  }
}
/**
 * @param {string} value - Value needed to test email validation
 * @param {number} maxChars - Maximum of number of characters allowed to pass validation
 * @param {function} t - translation
 * @param {string[]} errors - An array of errors to render the error message if it does not pass validation
 */
export function validateCharacterCount(value, maxChars, t, errors) {
  if (value && value.toString().length > maxChars) {
    errors.push(t('errors.maxCharacters', { maxChars: maxChars }));
  }
}

/**
 * @param {string} value - Value needed to test email validation
 * @param {{ type: string, param: any}[]} validations - Array of objects with type of validation and param if any
 * @param {function} t - translation
 * @param {string[]} errors - An array of errors to render the error message if it does not pass validation
 */
export function validateArrayValues(values, validations, t, errors) {
  if (values && Array.isArray(values) && values.length > 0) {
    values.forEach(value => {
      const error = validate(value, validations, false, t);
      if (error) {
        errors.push(error);
      }
    });
  }

  return errors;
}

export function hasFieldError({ fieldError, keys }) {
  let hasError = false;
  // check if field values are valid
  keys.forEach(val => {
    const errorObj = fieldError[val];

    if (errorObj instanceof String || typeof errorObj === 'string') {
      // if there is a value that isn't an object or array, it's an error message
      hasError = true;
    } else if (Array.isArray(errorObj) && errorObj.length > 0) {
      // find errors in the error array
      errorObj.forEach(arrayObj => {
        const nestedKeys = arrayObj ? Object.keys(arrayObj) : [];

        // find errors in the error object
        if (nestedKeys.length > 0) {
          hasError = hasFieldError({ fieldError: arrayObj, keys: nestedKeys });
          return;
        }
        hasError = false;
      });
      return;
    } else if (errorObj) {
      const nestedKeys = Object.keys(errorObj);

      // find errors in the error object
      if (nestedKeys.length > 0) {
        hasError = hasFieldError({ fieldError: errorObj, keys: nestedKeys });
      }
      return;
    }
  });

  return hasError;
}
